var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var _a;
import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, } from "react";
import { z } from "zod";
import { getClassification } from "@alveusgg/data/build/ambassadors/classification";
import allAmbassadors, { ambassadorSchema, } from "@alveusgg/data/build/ambassadors/core";
import { isActiveAmbassadorEntry } from "@alveusgg/data/build/ambassadors/filters";
import { ambassadorImageSchema, getAmbassadorImages, } from "@alveusgg/data/build/ambassadors/images";
import { getSpecies, speciesSchema, } from "@alveusgg/data/build/ambassadors/species";
import enclosures from "@alveusgg/data/build/enclosures";
import { getIUCNStatus } from "@alveusgg/data/build/iucn";
import { getToday } from "../utils/dateManager";
import { typeSafeObjectEntries, typeSafeObjectFromEntries, } from "../utils/helpers";
import winstonImage from "../assets/winston.png";
// These schema should match the type exposed by the API
const apiAmbassadorSchema = ambassadorSchema.extend({
    image: ambassadorImageSchema.extend({
        src: z.string().url(),
    }),
    species: speciesSchema.extend({
        key: z.string(),
        iucn: speciesSchema.shape.iucn.and(z.object({
            title: z.string(),
        })),
        class: z.object({
            key: z.string(),
            title: z.string(),
        }),
    }),
    enclosure: z.object({
        key: z.string(),
        title: z.string(),
    }),
});
// Use transform here so we parse each ambassador individually
const apiSchema = z.object({
    v4: z
        .record(z.string(), 
    // Use nullable here as the fallback for when we fail to parse an ambassador
    apiAmbassadorSchema.nullable().catch((ctx) => {
        console.error("Failed to parse ambassador", ctx.value, z.prettifyError(ctx.error));
        return null;
    }))
        .transform((val) => 
    // Filter out any null values that failed to parse
    typeSafeObjectFromEntries(Object.entries(val).filter((entry) => !!entry[1])))
        // Ensure we didn't fail to parse all ambassadors
        .refine((val) => Object.keys(val).length > 0, {
        message: "No ambassadors found",
    }),
});
const apiBaseUrl = (_a = process.env.REACT_APP_API_BASE_URL) === null || _a === void 0 ? void 0 : _a.replace(/\/+$/, "");
if (!apiBaseUrl)
    throw new Error("REACT_APP_API_BASE_URL environment variable is not set");
const fetchAmbassadors = () => __awaiter(void 0, void 0, void 0, function* () {
    const response = yield fetch(`${apiBaseUrl}/api/stream/ambassadors`);
    if (!response.ok)
        throw new Error(`Failed to fetch ambassadors: ${response.status} ${response.statusText} ${yield response.text()}`);
    const data = yield response.json();
    return apiSchema.parse(data).v4;
});
const fallbackAmbassadors = typeSafeObjectFromEntries(typeSafeObjectEntries(allAmbassadors)
    .filter(isActiveAmbassadorEntry)
    .map(([key, val]) => {
    const image = getAmbassadorImages(key)[0];
    const species = getSpecies(val.species);
    return [
        key,
        Object.assign(Object.assign({}, val), { image, species: Object.assign(Object.assign({}, species), { key: val.species, iucn: Object.assign(Object.assign({}, species.iucn), { title: getIUCNStatus(species.iucn.status) }), class: {
                    key: species.class,
                    title: getClassification(species.class),
                } }), enclosure: {
                key: val.enclosure,
                title: enclosures[val.enclosure].name,
            } }),
    ];
}));
// Use a context to fetch the ambassadors from the API
const Context = createContext(null);
export const AmbassadorsProvider = ({ children, }) => {
    const [ambassadors, setAmbassadors] = useState(null);
    // On mount, attempt to fetch the ambassadors from the API
    // If we can't fetch the ambassadors, use the data from the data package
    useEffect(() => {
        fetchAmbassadors()
            .catch((err) => {
            console.error(err);
            return fallbackAmbassadors;
        })
            .then(setAmbassadors);
    }, []);
    // Refresh the current ambassadors after a !refresh command
    const refresh = useCallback(() => {
        fetchAmbassadors()
            .then(setAmbassadors)
            .catch((err) => console.error(err));
    }, []);
    // Every 2 hours, attempt to fetch the ambassadors from the API
    // If we can't fetch the ambassadors, we'll just use the existing data
    useEffect(() => {
        const interval = setInterval(() => {
            fetchAmbassadors()
                .then(setAmbassadors)
                .catch((err) => console.error(err));
        }, 2 * 60 * 60 * 1000);
        return () => clearInterval(interval);
    }, []);
    return (_jsx(Context.Provider, { value: { ambassadors, refresh }, children: children }));
};
const winston = {
    name: "Winston",
    alternate: [],
    commands: ["winston"],
    species: {
        key: "polarBear",
        name: "Polar Bear",
        scientificName: "Twitchus memeticus",
        iucn: {
            id: null,
            assessment: null,
            title: getIUCNStatus("NE"),
            status: "NE",
        },
        native: "Twitch chat (including the Animals, Aquariums, & Zoos category), miscellaneous emote services",
        lifespan: {
            wild: "Unknown",
            captivity: "Unknown",
        },
        birth: "live",
        class: {
            key: "mammalia",
            title: getClassification("mammalia"),
        },
    },
    sex: "Male",
    birth: "2020-04-01",
    arrival: "2022-12-01",
    retired: null,
    enclosure: {
        key: "ice",
        title: "Ice Pool",
    },
    story: "Winston was rescued by the Ontario Zoo in Canada after it was noticed that he was watching streams too often and not touching grass. Originally on loan to Alveus for two years, he is now a permanent resident of Texas.",
    mission: "He is an ambassador for stream-life balance and encouraging all chatters to step away from their devices more often.",
    clips: [],
    homepage: null,
    plush: null,
    image: {
        src: winstonImage,
        alt: "Winston the polar bear",
        position: "50% 25%",
    },
};
const isWinstonDate = (date) => date === "04-01";
export const useAmbassadors = () => {
    const context = useContext(Context);
    // Setup a timer to store the current month and day
    const [date, setDate] = useState("");
    useEffect(() => {
        const updateDate = () => setDate(getToday().toFormat("MM-dd"));
        updateDate();
        const interval = setInterval(updateDate, 60 * 1000);
        return () => clearInterval(interval);
    }, []);
    // Return the ambassadors, with Winston added to the start if it's April 1st
    return useMemo(() => (context === null || context === void 0 ? void 0 : context.ambassadors)
        ? Object.assign(Object.assign({}, (isWinstonDate(date) ? { winston } : {})), context === null || context === void 0 ? void 0 : context.ambassadors) : null, [context === null || context === void 0 ? void 0 : context.ambassadors, date]);
};
export const useAmbassador = (key) => {
    const ambassadors = useAmbassadors();
    return ambassadors === null || ambassadors === void 0 ? void 0 : ambassadors[key];
};
export const useAmbassadorsRefresh = () => {
    const context = useContext(Context);
    return context === null || context === void 0 ? void 0 : context.refresh;
};
